import queryString from 'query-string'
import { SearchSearchParams } from '@/search-params-parser/search'
import { FoodParamType } from '@/constants/food-type'
import { ProductsQueryType } from '@/schemas/product/products-query'

type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'DELETE'

export type Endpoint = {
  url: string
  method: HttpMethod
  auth?: boolean
}

const createEndpoint = ({
  url,
  method,
  auth = false,
}: {
  url: string
  method: HttpMethod
  auth?: boolean
}): Endpoint => ({
  url,
  method,
  auth,
})

export const API_ENDPOINT = {
  auth: {
    // Swagger: https://api.ddokso.com/docs#/auth%20API
    signIn: () => createEndpoint({ url: '/api/auth/oauth', method: 'POST' }),
    refreshToken: () =>
      createEndpoint({ url: '/api/auth/refresh', method: 'POST' }),
  },
  user: {
    // Swagger: https://api.ddokso.com/docs#/users$20API
    getCurrentUser: () =>
      createEndpoint({ url: '/api/users/me', method: 'GET', auth: true }),
    patchUser: () =>
      createEndpoint({ url: '/api/users/me', method: 'PATCH', auth: true }),
    deleteUser: () =>
      createEndpoint({ url: '/api/users/me', method: 'DELETE', auth: true }),
    sendConfirmEmail: () =>
      createEndpoint({
        url: '/api/users/me/email-send',
        method: 'POST',
        auth: true,
      }),
    confirmEmail: () =>
      createEndpoint({
        url: '/api/users/me/email-confirm',
        method: 'POST',
      }),
  },

  category: {
    // Swagger: https://api.ddokso.com/docs#/categories%20API
    getCategories: () =>
      createEndpoint({ url: '/api/categories', method: 'GET' }),
    getCategory: (id: string) =>
      createEndpoint({ url: `/api/categories/${id}`, method: 'GET' }),
  },

  products: {
    // Swagger: https://api.ddokso.com/docs#/products%20API
    getProducts: ({
      page = 1,
      pageSize = 10,
      tag,
      sort = 'TagSort',
      state = 'Sale',
      categoryId,
      brandId,
    }: ProductsQueryType) => {
      const baseUrl = '/api/products'
      const url = queryString.stringifyUrl(
        {
          url: baseUrl,
          query: {
            page,
            pageSize,
            tag,
            sort,
            state,
            categoryId,
            brandId,
          },
        },
        { skipEmptyString: true, skipNull: true },
      )
      return createEndpoint({ url, method: 'GET' })
    },
    getProduct: (id: string) =>
      createEndpoint({ url: `/api/products/${id}`, method: 'GET' }),
    getProductOptions: (id: string) =>
      createEndpoint({ url: `/api/products/${id}/options`, method: 'GET' }),
    getPopularProducts: (id: string) =>
      createEndpoint({ url: `/api/products/${id}/populars`, method: 'GET' }),
    getProductPriceHistories: ({
      id,
      startDate,
      endDate,
    }: {
      id: string
      startDate: string
      endDate: string
    }) => {
      const baseUrl = `/api/products/${id}/price-histories`
      const url = queryString.stringifyUrl(
        {
          url: baseUrl,
          query: {
            stateDate: startDate,
            endDate,
          },
        },
        { skipEmptyString: true, skipNull: true },
      )
      return createEndpoint({ url, method: 'GET' })
    },

    getProductNotificationState: (id: string) =>
      createEndpoint({
        url: `/api/products/${id}/state`,
        method: 'GET',
        auth: true,
      }),
  },

  notification: {
    // Swagger: https://api.ddokso.com/docs#/notification%20API
    createNotification: () =>
      createEndpoint({ url: '/api/notifications', method: 'POST', auth: true }),
    getNotifications: ({
      page = 1,
      pageSize = 10,
      sort = 'RECENT',
      isPurchaseTiming = false,
    }: {
      page?: number
      pageSize?: number
      sort?:
        | 'RECENT' // 최신순
        | 'DISCOUNT' // 할인율순
        | 'LOW_PRICE' // 낮은가격순
      isPurchaseTiming?: boolean
    }) => {
      const baseUrl = '/api/notifications'
      const url = queryString.stringifyUrl(
        {
          url: baseUrl,
          query: {
            page,
            pageSize,
            sort,
            isPurchaseTiming,
          },
        },
        { skipEmptyString: true, skipNull: true },
      )
      return createEndpoint({ url, method: 'GET', auth: true })
    },
    updateNotification: () =>
      createEndpoint({
        url: '/api/notifications',
        method: 'PATCH',
        auth: true,
      }),
    deleteNotification: () =>
      createEndpoint({
        url: '/api/notifications',
        method: 'DELETE',
        auth: true,
      }),
    getNotifiedProducts: () =>
      createEndpoint({
        url: '/api/notifications/products',
        method: 'GET',
        auth: true,
      }),
  },

  search: {
    // Swagger: https://api.ddokso.com/docs#/search%20API
    searchProducts: (props: SearchSearchParams) => {
      const baseUrl = '/api/search'
      const url = queryString.stringifyUrl(
        {
          url: baseUrl,
          query: {
            ...props,
          },
        },
        { skipEmptyString: true, skipNull: true },
      )
      return createEndpoint({ url, method: 'GET' })
    },
    autoComplete: ({ q }: { q: string }) => {
      const baseUrl = '/api/search/auto-completes'
      const url = queryString.stringifyUrl(
        {
          url: baseUrl,
          query: {
            q,
          },
        },
        { skipEmptyString: true, skipNull: true },
      )
      return createEndpoint({ url, method: 'GET' })
    },
    link: ({ q }: { q: string }) => {
      const baseUrl = '/api/search/url'
      const url = queryString.stringifyUrl(
        {
          url: baseUrl,
          query: {
            q,
          },
        },
        { skipEmptyString: true, skipNull: true },
      )
      return createEndpoint({ url, method: 'GET' })
    },
  },

  smartPick: {
    // Swagger: https://api.ddokso.com/docs#/smart-picks%20API
    getSmartPickProducts: () =>
      createEndpoint({ url: '/api/smart-pick/products', method: 'GET' }),
    getSmartPickKeywords: () =>
      createEndpoint({ url: '/api/smart-pick/keywords', method: 'GET' }),
  },

  landing: {
    // Swagger: https://api.ddokso.com/docs#/landings%20API
    getMain: () => createEndpoint({ url: '/api/landings/main', method: 'GET' }),
    getMainV2: () =>
      createEndpoint({ url: '/api/landings/main-new', method: 'GET' }),
    getBrandHall: () =>
      createEndpoint({ url: '/api/landings/brand-hall', method: 'GET' }),
    getSnackHall: () =>
      createEndpoint({ url: '/api/landings/snack-hall', method: 'GET' }),
    getDrinkHall: () =>
      createEndpoint({ url: '/api/landings/drink-hall', method: 'GET' }),
    getFoodHall: ({ type }: { type: FoodParamType }) =>
      createEndpoint({ url: `/api/landings/${type}-hall`, method: 'GET' }),
  },

  brand: {
    // Swagger: https://api.ddokso.com/docs#/brands%20api
    getBrands: () => createEndpoint({ url: '/api/brands', method: 'GET' }),
    getBrandById: (id: string) =>
      createEndpoint({ url: `/api/brands/${id}`, method: 'GET' }),
  },
}
